import { getCsrfValue } from "~/common/utils/ApiHelper";
import { CAPTCHA_SITE_KEY_FIELD } from "~/utils/constants";
import { resolveApiUrl } from "~/utils/url/urlResolver";

export interface ResponseData {
  globalErrors: string[];
  validationResults: { [fieldName: string]: string[] };
}

const FALLBACK_ERROR_KEY = "form.global.error";
const apiUrl = resolveApiUrl("/login/pw/request");

function normalizeResponseData(data: unknown): ResponseData {
  if (!data || typeof data !== "object") {
    return { globalErrors: [FALLBACK_ERROR_KEY], validationResults: {} };
  }

  const candidate = data as Partial<ResponseData>;
  const validationResults =
    candidate.validationResults && typeof candidate.validationResults === "object" ? candidate.validationResults : {};

  return {
    globalErrors: Array.isArray(candidate.globalErrors) ? candidate.globalErrors : [FALLBACK_ERROR_KEY],
    validationResults,
  };
}

async function parseResetPasswordResponse(response: Response): Promise<ResponseData> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error(FALLBACK_ERROR_KEY);
  }

  return normalizeResponseData(await response.json());
}

export function sendResetPasswordRequest(email: string, captchaValue: string): Promise<ResponseData> {
  const params = new URLSearchParams();
  params.set("email", email);
  params.set(CAPTCHA_SITE_KEY_FIELD, captchaValue);

  return fetch(apiUrl, {
    body: params.toString(),
    cache: "no-cache",
    credentials: "same-origin",
    method: "POST",
    mode: "cors",
    headers: {
      Accept: "application/json",
      dataType: "json",
      "content-type": "application/x-www-form-urlencoded",
      CSRFToken: getCsrfValue(),
      "X-Requested-With": "XMLHttpRequest",
    },
    redirect: "follow",
  }).then(async (response) => {
    const data = await parseResetPasswordResponse(response);
    if (!response.ok && data.globalErrors.length === 0 && Object.keys(data.validationResults).length === 0) {
      data.globalErrors.push(FALLBACK_ERROR_KEY);
    }
    return data;
  });
}
